/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { useMemo, type FC } from 'react'
import { Button, Card, Col, Form, Row, Stack } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import FileUploader from '../FileUploader'

const validationSchema = yup.object().shape({
  id: yup.number().positive('O ID deve ser um número positivo').nullable(),
  name: yup
    .string()
    .required('O nome é obrigatório')
    .default('')
    .min(0, 'O nome deve ter no mínimo 0 caracteres')
    .max(255, 'O nome deve ter no máximo 255 caracteres'),
  placeholder: yup
    .string()
    .nullable()
    .min(0, 'O nome deve ter no mínimo 0 caracteres')
    .max(255, 'O nome deve ter no máximo 255 caracteres'),
  slug: yup
    .string()
    .required('O slug é obrigatório')
    .default('')
    .min(0, 'O slug deve ter no mínimo 0 caracteres')
    .max(255, 'O slug deve ter no máximo 255 caracteres'),
  icon: yup.mixed().notRequired(),
  status: yup
    .string()
    .oneOf(['active', 'inactive', 'deleted'], 'Situação inválida')
    .default('active')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormProductSectionHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormProductSectionHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean
}

/**
 * FormProductSection is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormProductSection from './src/views/PostEdit/FormProductSection.tsx'
 *
 * const MyComponent = () => (
 *   <FormProductSection />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormProductSection: FC<Props> = ({ onSubmit = () => {}, initialValues = {}, isLoading = false }) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        {formik?.values?.id !== undefined && (
          <Form.Group as={Col} md={12} className="mb-3">
            <Form.Label>ID</Form.Label>
            <Form.Control
              type='text'
              placeholder='Código da seção do produto'
              name='id'
              value={formik?.values?.id?.toString() ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </Form.Group>
        )}
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type='text'
            placeholder='Informe o nome da seção do produto'
            name='name'
            value={formik.values?.name ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Apelido</Form.Label>
          <Form.Control
            type='text'
            placeholder='O apelido é o nome que será exibido na tela no lugar do nome'
            name='placeholder'
            value={formik.values?.placeholder ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Slug</Form.Label>
          <Form.Control
            type='text'
            placeholder='O slug é o identificador único da seção do produto'
            name='slug'
            value={formik.values?.slug ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Ícone</Form.Label>
          <Stack direction="horizontal" gap={1}>
            {typeof formik.values?.icon === 'string' && (
              <Card.Img
                variant="top"
                src={formik.values?.icon}
                style={{
                  width: 80,
                  height: 80,
                  border: '1px solid #ccc',
                  padding: 5,
                  borderRadius: 5,
                }}
              />
            )}
            <div className="vr" />
            <FileUploader
              variant='outline-primary'
              accept='.svg'
              size='sm'
              className="me-auto"
              disabled={formik.isSubmitting || isLoading}
              onReceiveFiles={([{ file }]) => {
                formik.setFieldValue('icon', file)
              }}
            >
              <FontAwesomeIcon icon={faUpload} />
              {' '}
              Enviar arquivo
            </FileUploader>
          </Stack>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Visibilidade</Form.Label>
          <Form.Select
            value={formik.values.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='active'>🌎 Pública</option>
            <option value='inactive'>🔒 Privada</option>
          </Form.Select>
        </Form.Group>
        <Col md={12} className="mb-3">
          <Button
            variant="link"
            style={{ marginRight: 10 }}
            onClick={() => navigate('/product-sections')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button type="submit" disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}>
            Salvar
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default FormProductSection
